import { FormEvent, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { PageFrame } from '../components/PageFrame';
import { useAuth } from '../contexts/AuthContext';
import { formatPlanPrice, getQuestionPlan } from '../data/plans';
import { CheckoutUnavailableError, createQuestionPackCheckout } from '../lib/checkout';

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const plan = useMemo(() => getQuestionPlan(searchParams.get('plan')), [searchParams]);
  const [clientCheckoutKey] = useState(() => (
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`
  ));
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!plan) return <Navigate to="/pricing" replace />;
  if (!authLoading && !user) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!consent) {
      setError('Confirm the digital service acknowledgement to continue.');
      return;
    }
    setBusy(true);
    try {
      const checkoutUrl = await createQuestionPackCheckout(plan.id, clientCheckoutKey);
      window.location.assign(checkoutUrl);
    } catch (checkoutError) {
      setError(
        checkoutError instanceof CheckoutUnavailableError
          ? checkoutError.message
          : checkoutError instanceof Error
            ? checkoutError.message
            : 'We could not start checkout. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageFrame>
      <section className="checkout-shell section-shell">
        <div className="checkout-copy">
          <p className="eyebrow">Secure checkout</p>
          <h1>Your question pack</h1>
          <p>Review the package linked to your signed-in Sanjeom account.</p>
          <Link className="text-link" to="/pricing">← Change package</Link>
        </div>
        <form className="checkout-card" onSubmit={submit}>
          <div className="checkout-summary">
            <div>
              <p>{plan.name}</p>
              <strong>{plan.questions} AI questions</strong>
            </div>
            <strong>{formatPlanPrice(plan)}</strong>
          </div>
          <div className="checkout-account">
            <span>Questions will be added to</span>
            <strong>{user?.email || user?.display_name || 'your signed-in account'}</strong>
          </div>
          <label className="consent-row">
            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
            <span>I request immediate access to this digital service and understand that using the pack can affect my cancellation right.</span>
          </label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button button--warm button--full" type="submit" disabled={busy}>
            {busy ? 'Preparing secure checkout…' : `Continue — ${formatPlanPrice(plan)}`}
          </button>
          <p className="checkout-disclosure">
            Payment is processed by Stripe. We do not receive or store your full card details. By continuing, you agree to our{' '}
            <Link to="/terms">Terms</Link> and <Link to="/refund-policy">Refund Policy</Link>.
          </p>
        </form>
      </section>
    </PageFrame>
  );
}
